import { FirestorePath, NameValuePair, isDefined } from "@zickko/shared";
import {
  OrderByDirection,
  WhereFilterOp,
  collectionGroup as _collectionGroup,
  limit as _limit,
  orderBy as _orderBy,
  query as _query,
  where as _where,
  onSnapshot,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { firebaseFirestore } from "../services/firebaseFirestore";
import { toastStore } from "../utils/toastStore";
import { useDeepDeps } from "../utils/useDeepDeps";

export const useCollectionGroupBind = <K extends NameValuePair<string>, T>(
  firestorePath: FirestorePath<K, T>,
  args: {
    key: K;
    where?: [Extract<keyof T, string>, WhereFilterOp, unknown][];
    orderByField: Extract<keyof T, string>;
    orderByDirection: OrderByDirection;
    limit?: number;
  }
) => {
  //
  const { key, orderByDirection, orderByField, limit = 100, where } = args;
  // Initial state is undefined, which should be the loading state
  const [value, setValue] = useState<T[] | undefined>(undefined);

  useEffect(() => {
    //
    const path = firestorePath.path(key);

    // create query
    let query = _query(
      _collectionGroup(firebaseFirestore, path),
      _orderBy(orderByField, orderByDirection),
      _limit(limit)
    );

    // where clause and the value is defined
    if (where) {
      if (where.length > 3) {
        console.error(
          `Cannot have more than 3 items in where clause, please remodel the data to be more optimistic for querying.`
        );
      }
      for (const w of where) {
        if (w && isDefined(w[2])) query = _query(query, _where(w[0], w[1], w[2]));
      }
    }

    return onSnapshot(
      query,
      (snap) => {
        console.debug(`[bindCollectionGroup] On snapshot listener:`, firestorePath, snap);
        if (snap.empty) {
          setValue([]);
          return;
        }
        setValue(snap.docs.map((e) => e.data() as T));
      },
      (error) => {
        console.error(firestorePath, error);
        toastStore.error(error.message);
        setValue([]);
      }
    );
  }, [useDeepDeps(key)]);

  return { value };
};
